/**
 * Keyboard-wedge transport: balances in "Quick USB" / HID keyboard mode type the reading and press Enter
 * when PRINT is pressed. Works in every browser (including iPad over USB-C). A line is a burst of
 * characters typed faster than a person types, ending with Enter; slower typing is left alone.
 */
import type { BalanceTransport, TransportCallbacks } from "./types";

export interface KeyboardWedgeOptions {
  /** Largest gap between two characters of one reading. */
  maxGapMs: number;
  /** Where to listen; the window by default. */
  target: Pick<Window, "addEventListener" | "removeEventListener">;
  now: () => number;
}

const READING_CHARACTERS = /^[0-9A-Za-z+\-.,: "%]$/;

export function createKeyboardWedgeTransport(options: Partial<KeyboardWedgeOptions> = {}): BalanceTransport {
  const { maxGapMs = 60, target = window, now = () => performance.now() } = options;
  let buffer = "";
  let lastKeyAt = -Infinity;
  let callbacks: TransportCallbacks | null = null;

  function onKeyDown(event: Event) {
    const { key } = event as KeyboardEvent;
    const at = now();
    if (key === "Enter") {
      const line = buffer;
      buffer = "";
      if (line && /\d/.test(line) && at - lastKeyAt <= maxGapMs) {
        event.preventDefault();
        callbacks?.onLine(line);
      }
      return;
    }
    if (key.length !== 1 || !READING_CHARACTERS.test(key)) return;
    if (at - lastKeyAt > maxGapMs) buffer = "";
    buffer += key;
    lastKeyAt = at;
  }

  return {
    kind: "keyboard",
    label: "Keyboard (Quick USB)",
    canWrite: false,
    open(nextCallbacks) {
      callbacks = nextCallbacks;
      target.addEventListener("keydown", onKeyDown, true);
      return Promise.resolve();
    },
    write() {
      return Promise.reject(new Error("A balance in keyboard mode can't receive commands"));
    },
    close() {
      target.removeEventListener("keydown", onKeyDown, true);
      const closing = callbacks;
      callbacks = null;
      closing?.onClose();
      return Promise.resolve();
    },
  };
}
